import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

import { ApiError } from '@/api/client';
import type { Cart, CartShippingUpdate } from '@/features/storefront/api/storefront';
import { cartTokenFor } from '@/features/storefront/cartToken';
import { useStorefrontData, useStorefrontScope } from '@/features/storefront/data/context';
import type { StorefrontDataSource } from '@/features/storefront/data/source';

/**
 * Loading the basket of whichever shop this is.
 *
 * The source and the token store are both taken from context rather than
 * imported, which is what lets a theme preview keep a working cart of its own
 * without ever touching the merchant's. Everything else - the 404-means-start-
 * a-new-basket rule especially - is the behaviour the live shop always had.
 */
function loader(source: StorefrontDataSource) {
  const store = cartTokenFor(source.key);

  return async function loadCart(): Promise<Cart> {
    const token = store.get();
    if (token) {
      try {
        return await source.fetchCart(token);
      } catch (error) {
        // A converted or expired cart is not an error for the shopper - it just
        // means they need a fresh basket.
        if (!(error instanceof ApiError) || error.status !== 404) throw error;
        store.clear();
      }
    }
    const created = await source.createCart();
    store.set(created.token);
    return created;
  };
}

/** The cache key of this shop's basket. */
function cartKey(scope: [string, string]): [string, string, string] {
  return [...scope, 'cart'];
}

export function useCart() {
  const source = useStorefrontData();
  const scope = useStorefrontScope();
  const load = useMemo(() => loader(source), [source]);

  return useQuery({
    queryKey: cartKey(scope),
    queryFn: load,
    staleTime: 0,
    // A language switch re-keys the basket (its line names are localised) but
    // it is the same basket: keep showing it while the new names arrive,
    // rather than blanking the cart and the till for a round trip.
    placeholderData: keepPreviousData,
  });
}

export function useCartMutations() {
  const queryClient = useQueryClient();
  const source = useStorefrontData();
  const scope = useStorefrontScope();
  const load = useMemo(() => loader(source), [source]);
  const put = useCallback(
    (cart: Cart) => queryClient.setQueryData(cartKey(scope), cart),
    [queryClient, scope],
  );

  const setItem = useMutation({
    mutationFn: async ({ variantId, quantity }: { variantId: string; quantity: number }) => {
      const cart = await load();
      return source.setCartItem(cart.token, variantId, quantity);
    },
    onSuccess: put,
  });

  const discount = useMutation({
    mutationFn: async (code: string | null) => {
      const cart = await load();
      return source.applyDiscount(cart.token, code);
    },
    onSuccess: put,
  });

  const giftCard = useMutation({
    mutationFn: async (code: string | null) => {
      const cart = await load();
      return source.applyGiftCard(cart.token, code);
    },
    onSuccess: put,
  });

  // Where the basket is going, or which way. The cart comes back re-priced
  // for it, and the totals every page reads are the ones the server will
  // charge - which is why this writes the cache rather than a local number.
  const setShipping = useMutation({
    mutationFn: async (update: CartShippingUpdate) => {
      const cart = await load();
      return source.setCartShipping(cart.token, update);
    },
    onSuccess: put,
  });

  // The shopper's order instructions. Kept on the server's cart, so the
  // quick-cart drawer, a reload and the till all read the same note.
  const setNote = useMutation({
    mutationFn: async (note: string | null) => {
      const cart = await load();
      return source.setCartNote(cart.token, note);
    },
    onSuccess: put,
  });

  return { setItem, discount, giftCard, setShipping, setNote };
}

/**
 * Put one more of a variant in the cart, keeping whatever is already there.
 *
 * The quick-add behind a "Buy it again" or "Add to cart" button on a tile,
 * where there is no quantity box and no variant picker: the shopper means "one
 * more of this", not "make it exactly one". `setItem` sets an absolute
 * quantity, so the current line has to be read first - which is why this is a
 * mutation of its own rather than a call site convention.
 */
export function useAddToCart() {
  const queryClient = useQueryClient();
  const source = useStorefrontData();
  const scope = useStorefrontScope();
  const load = useMemo(() => loader(source), [source]);

  return useMutation({
    mutationFn: async ({ variantId, quantity = 1 }: { variantId: string; quantity?: number }) => {
      const cart = await load();
      const existing = cart.items.find((item) => item.variant_id === variantId);
      return source.setCartItem(cart.token, variantId, (existing?.quantity ?? 0) + quantity);
    },
    onSuccess: (cart) => queryClient.setQueryData(cartKey(scope), cart),
  });
}

/** The token store of the shop being rendered, for checkout to spend. */
export function useCartTokenStore() {
  const source = useStorefrontData();
  return useMemo(() => cartTokenFor(source.key), [source]);
}
